import { makeAutoObservable, runInAction } from 'mobx';
import { soloPlayApi, type SoloContext } from '@shared/api';
import { getGameResourceOwner } from '@shared/lib/auth/game-session';
import { apiImageUrl } from '@shared/lib/media/image-url';

type Pair = { exp: number; coins: number };
export class SoloProgressStore {
  loaded = false;
  error: string | null = null;
  currentQuestionId: string | undefined;
  solvedCount = 0;
  correctAnswers = 0;
  correctStreak = 0;
  bestDistance = 0;
  bestCorrectStreak = 0;
  categoryRecords: Record<string, { bestDistance: number; bestCorrectStreak: number }> = {};
  currentKind: string | undefined;
  runKind: string | undefined;
  revealedCorrect: boolean | undefined;
  revealedTitleId: string | undefined;
  questionErrors = new Map<string, string>();
  private questionSources = new Map<string, unknown>();
  private questionRequests = new Map<string, Promise<void>>();
  totalQuestions: number | undefined;
  complete = false;
  locked = false;
  status = '';
  accessSource: 'free' | 'purchased' | 'premium' | 'locked' = 'locked';
  earnedRewards: Pair = { exp: 0, coins: 0 };
  totalRewards: Pair | undefined;
  dailyEarnedExp = 0;
  dailyExpCap = 50;
  dailyResetAt = '';
  questions: Awaited<ReturnType<SoloProgressStore['loadQuestion']>>[] = [];
  private revision = 0;
  private request: Promise<void> | null = null;
  private summaryRequest: Promise<void> | null = null;
  constructor(
    readonly context: SoloContext,
    readonly login: string,
  ) {
    makeAutoObservable<this, 'request' | 'summaryRequest' | 'questionSources' | 'questionRequests'>(
      this,
      { request: false, summaryRequest: false, questionSources: false, questionRequests: false },
      { autoBind: true },
    );
  }
  get active() {
    return getGameResourceOwner() === this.login;
  }
  get dailyRewardsReceived() {
    return !!this.dailyResetAt && this.dailyEarnedExp >= this.dailyExpCap;
  }
  get remainingDailyRewards() {
    return { experience: Math.max(0, this.dailyExpCap - this.dailyEarnedExp), coins: 0 };
  }
  async loadQuestion(value: unknown) {
    const q = await soloPlayApi.question(value);
    return {
      id: q.id,
      difficulty: q.difficulty,
      options: q.options,
      optionTitleIds: q.optionTitleIds,
      serverBacked: true as const,
      frameUrl: apiImageUrl(q.screenshots[0]),
      extraFrameUrl: apiImageUrl(q.screenshots[1]),
      frameLabel: 'Кадр для вопроса',
      extraFrameLabel: 'Дополнительный кадр для вопроса',
    };
  }
  rememberQuestion(value?: unknown): Promise<void> {
    if (!value || !this.active) return Promise.resolve();
    const id = typeof value === 'string' ? value : typeof value === 'object' && 'id' in value ? String(value.id) : '';
    if (!id) return Promise.resolve();
    if (typeof value !== 'string') {
      this.questionSources.set(id, value);
      while (this.questionSources.size > 3) this.questionSources.delete(this.questionSources.keys().next().value!);
    }
    if (this.questions.some((q) => q.id === id)) return Promise.resolve();
    const pending = this.questionRequests.get(id);
    if (pending) return pending;
    this.questionErrors.delete(id);
    const request = this.loadQuestion(this.questionSources.get(id) ?? id)
      .then((question) => {
        if (this.active)
          runInAction(() => {
            this.questions = [...this.questions.filter((q) => q.id !== question.id).slice(-1), question];
            this.questionErrors.delete(id);
          });
      })
      .catch(() => {
        if (this.active)
          runInAction(() => {
            this.questionErrors.set(id, 'Не удалось загрузить варианты ответа.');
          });
      })
      .finally(() => {
        this.questionRequests.delete(id);
      });
    this.questionRequests.set(id, request);
    return request;
  }
  applyPack(state: Awaited<ReturnType<typeof soloPlayApi.packState>>) {
    if (!this.active) return;
    this.revision++;
    this.currentQuestionId = state.currentQuestionId;
    this.solvedCount = state.solvedQuestionIds.length;
    this.correctAnswers = this.solvedCount;
    this.totalQuestions = state.totalQuestions;
    this.complete = state.status === 'completed' || this.solvedCount === state.totalQuestions;
    this.accessSource = state.accessSource;
    this.locked = state.accessSource === 'locked';
    this.status = state.status;
    this.earnedRewards = state.earnedRewards;
    this.totalRewards = state.totalRewards;
    this.loaded = true;
    this.error = null;
  }
  applyEndless(state: Awaited<ReturnType<typeof soloPlayApi.endlessStart>>) {
    if (!this.active) return;
    this.revision++;
    this.currentQuestionId =
      state.question?.id ??
      (['reveal', 'paused'].includes(state.run?.status ?? '') ? this.currentQuestionId : undefined);
    this.runKind = state.run?.kind || undefined;
    this.currentKind = state.run?.kind || state.question?.kind;
    this.revealedCorrect = undefined;
    this.revealedTitleId = undefined;
    if (state.question) this.questionSources.set(state.question.id, state.question);
    this.solvedCount = state.run?.roundsCompleted ?? 0;
    this.correctAnswers = state.run?.correctAnswers ?? 0;
    this.correctStreak = state.run?.correctStreak ?? 0;
    this.status = state.run?.status ?? '';
    this.complete = state.run?.status === 'finished';
    this.earnedRewards = state.run?.earnedRewards ?? { exp: 0, coins: 0 };
    this.loaded = true;
    this.error = null;
  }
  applyEndlessAbort(state: Awaited<ReturnType<typeof soloPlayApi.endlessAbort>>) {
    if (!this.active) return;
    this.applyEndless({ run: state.run });
    this.complete = true;
    this.status = 'finished';
    this.currentQuestionId = undefined;
    this.questions = [];
  }
  applyPackAnswer(state: Awaited<ReturnType<typeof soloPlayApi.packAnswer>>) {
    if (this.context.mode !== 'pack') throw new Error('Pack answer in endless session');
    this.applyPack({ ...state, packId: this.context.packId, accessSource: this.accessSource });
  }
  applyEndlessAnswer(state: Awaited<ReturnType<typeof soloPlayApi.endlessAnswer>>) {
    this.applyEndless(state);
    this.applyDaily(state);
    this.complete = state.finished;
  }
  applyRecords(state: Awaited<ReturnType<typeof soloPlayApi.endlessState>>) {
    this.bestDistance = state.bestDistance;
    this.bestCorrectStreak = state.bestCorrectStreak;
    this.categoryRecords = state.categories ?? {};
  }
  applyDaily(state: { dailyEarnedExp: number; dailyExpCap: number; dailyResetAt: string }) {
    this.dailyEarnedExp = state.dailyEarnedExp;
    this.dailyExpCap = state.dailyExpCap;
    this.dailyResetAt = state.dailyResetAt;
  }
  refreshSummary(): Promise<void> {
    if (this.context.mode !== 'endless' || !this.active) return Promise.resolve();
    if (this.summaryRequest) return this.summaryRequest;
    const revision = this.revision;
    this.summaryRequest = soloPlayApi
      .endlessState()
      .then((state) => {
        if (this.active && this.revision === revision)
          runInAction(() => {
            this.applyDaily(state);
            this.applyRecords(state);
          });
      })
      .catch(() => {
        /* Keep the last confirmed summary on an optional background refresh failure. */
      })
      .finally(() => {
        this.summaryRequest = null;
      });
    return this.summaryRequest;
  }
  refresh(force = false, { question = true }: { question?: boolean } = {}): Promise<void> {
    if (this.request) return this.request;
    if (this.loaded && !force) return Promise.resolve();
    const revision = this.revision;
    this.request = (async () => {
      try {
        if (this.context.mode === 'pack') {
          const state = await soloPlayApi.packState(this.context.packId);
          if (this.revision === revision) runInAction(() => this.applyPack(state));
          if (question) await this.rememberQuestion(state.currentQuestionId);
        } else {
          const state = await soloPlayApi.endlessState();
          if (this.revision === revision)
            runInAction(() => {
              this.applyEndless(state);
              this.revealedCorrect = state.correct;
              this.revealedTitleId = state.correctTitleId;
              this.applyDaily(state);
              this.applyRecords(state);
            });
          if (question) await this.rememberQuestion(state.question);
        }
      } catch {
        if (this.active)
          runInAction(() => {
            this.error = 'Не удалось загрузить сохранённую игру. Попробуйте ещё раз.';
          });
      } finally {
        runInAction(() => {
          this.request = null;
        });
      }
    })();
    return this.request;
  }
}
const stores = new Map<string, SoloProgressStore>();
export const getSoloProgress = (context: SoloContext, login: string) => {
  const key = `${login}:${context.mode}:${context.mode === 'pack' ? context.packId : ''}`;
  if (!stores.has(key)) stores.set(key, new SoloProgressStore(context, login));
  return stores.get(key)!;
};
